import { Navigate, useLocation } from 'react-router';
import type { ReactNode } from 'react';
import { useSession } from '../lib/session.js';

/**
 * Route guard. Redirects when there is no session, and keeps staff and portal
 * actors in their own tree.
 *
 * This is convenience, not security. The real boundary is in Postgres: bypassing
 * this component gets you a page that renders zeros, because every query behind it
 * runs through an RLS-scoped connection and `/api/staff/*` endpoints additionally
 * refuse non-staff at the API. Treating a client-side check as protection is how
 * SPAs leak data — so nothing here is load-bearing.
 */
export function RequireAuth({
  children,
  require,
}: {
  children: ReactNode;
  /** Which tree this is. Portal accepts owners and tenants. */
  require: 'staff' | 'portal';
}) {
  const { state } = useSession();
  const location = useLocation();

  if (state.status === 'loading') {
    return <p className="muted shell__main">Checking your session…</p>;
  }

  if (state.status === 'signed-out') {
    // Preserve where they were headed so sign-in can return them there.
    return (
      <Navigate
        to={require === 'staff' ? '/staff/login' : '/login'}
        replace
        state={{ from: location.pathname }}
      />
    );
  }

  const isStaff = state.me.actor.actorType === 'STAFF';

  if (require === 'staff' && !isStaff) {
    return <Navigate to="/portal" replace />;
  }
  if (require === 'portal' && isStaff) {
    // Staff have their own console; sending them to a tenant-shaped page would
    // just show them the portfolio through the wrong lens.
    return <Navigate to="/staff" replace />;
  }

  return <>{children}</>;
}
